/** Raster tier selection and decode budget (pure — unit tested). */
import { SHEET_TIER_WIDTH, type SheetTier } from '../../shared/dto/library';

export const TIER_ORDER: readonly SheetTier[] = ['s', 'm', 'l'];

export function tierRank(t: SheetTier): number {
  return TIER_ORDER.indexOf(t);
}

/** Required raster width for a page shown `pageCssWidth` CSS px wide at `zoom` (10 % slack). */
export function requiredPixelWidth(pageCssWidth: number, dpr: number, zoom: number): number {
  return Math.max(0, pageCssWidth) * Math.max(1, dpr || 1) * Math.max(1, zoom || 1) * 0.9;
}

/**
 * Smallest available tier whose width ≥ the required pixel width; the largest available tier when
 * none is wide enough; null when nothing is available.
 */
export function chooseTier(available: readonly SheetTier[], requiredPx: number): SheetTier | null {
  const tiers = TIER_ORDER.filter((t) => available.includes(t));
  if (tiers.length === 0) return null;
  for (const t of tiers) if (SHEET_TIER_WIDTH[t] >= requiredPx) return t;
  return tiers[tiers.length - 1]!;
}

/** Never downgrade a tier that is already loaded (avoids refetching after rotation / zoom out). */
export function upgradeOnly(current: SheetTier | null, wanted: SheetTier | null): SheetTier | null {
  if (!wanted) return current;
  if (!current) return wanted;
  return tierRank(wanted) > tierRank(current) ? wanted : current;
}

/**
 * Decoded-megapixel budget for large page images: ≈ 48 MP on 2732-px iPads (12.9"/13"), ≈ 24 MP
 * otherwise. Uses the longest screen side because iPadOS reports screen.width in portrait.
 */
export function decodeBudgetMp(screenW: number, screenH: number, dpr: number): number {
  const longest = Math.max(screenW, screenH) * Math.max(1, dpr || 1);
  return longest >= 2700 ? 48 : 24;
}

/** Estimated raster size of a page at a tier (before the bundle header is known). */
export function estimatePixels(tier: SheetTier, page: { w: number; h: number }): { pw: number; ph: number } {
  const pw = SHEET_TIER_WIDTH[tier];
  const ratio = page.w > 0 ? page.h / page.w : Math.SQRT2;
  return { pw, ph: Math.round(pw * ratio) };
}
